import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import RegionPicker from '../components/RegionPicker';
import DataManagement from './DataManagement';
import CustomTables from './CustomTables';
import JoinedSources from './JoinedSources';
import TableMapping from './TableMapping';
import { useFormDirty } from '../hooks/useFormDirty';

/* Standalone-safe apiFetch (works in both bizsuite-app and admin entry) */
async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('bizsuite_token');
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    localStorage.removeItem('bizsuite_token');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }
  return res;
}

const USER_ROLE_OPTIONS = ['super_admin', 'tenant_admin', 'manager', 'consultant', 'viewer'];

const EMPTY_USER = {
  email: '', first_name: '', last_name: '', role: 'consultant', status: 'active', password: ''
};

const ALL_PERMISSIONS = [
  'crm:read', 'crm:write', 'crm:delete',
  'customers:read', 'customers:write', 'customers:delete',
  'projects:read', 'projects:write', 'projects:delete',
  'billing:read', 'billing:write',
  'admin:read', 'admin:write',
  'reports:read', 'reports:export'
];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');
  const TABS = ['users', 'tenants', 'roles', 'settings', 'data', 'custom-tables', 'joined-sources', 'table-mapping'];
  const TAB_LABELS = { users: 'Users', tenants: 'Tenants', roles: 'Roles', settings: 'Settings', data: 'Data Management', 'custom-tables': 'Custom Tables', 'joined-sources': 'Joined Sources', 'table-mapping': 'Table Mapping' };

  return (
    <div>
      <div className="tabs">
        {TABS.map(tab => (
          <div
            key={tab}
            className={`tab${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
          </div>
        ))}
      </div>

      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'tenants' && <TenantsTab />}
      {activeTab === 'roles' && <RolesTab />}
      {activeTab === 'settings' && <SettingsTab />}
      {activeTab === 'data' && <DataManagement />}
      {activeTab === 'custom-tables' && <CustomTables />}
      {activeTab === 'joined-sources' && <JoinedSources />}
      {activeTab === 'table-mapping' && <TableMapping />}
    </div>
  );
}

/* ================================================================
   Users Tab
   ================================================================ */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_USER });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data.data || data.users || data || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_USER });
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function openEdit(user) {
    setEditing(user);
    // DB stores full_name as single field; split for form display
    const nameParts = (user.full_name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    setForm({
      email: user.email || '',
      first_name: firstName,
      last_name: lastName,
      role: user.role || 'consultant',
      status: user.status || 'active',
      password: ''
    });
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    markDirty();
  }

  async function handleSave() {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email is required';
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    if (!editing && !form.password.trim()) errs.password = 'Password is required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);

    // Backend expects: fullName (combined), roleId (not role), password (on create)
    const body = {
      email: form.email,
      fullName: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
      roleId: form.role || null,
      status: form.status || 'active',
    };
    if (!editing) {
      body.password = form.password;
    }

    try {
      const url = editing ? `/api/admin/users/${editing.id}` : '/api/admin/users';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        resetDirty();
        setModalOpen(false);
        fetchUsers();
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || data.message || 'Failed to save user' });
      }
    } catch (e) {
      setErrors({ _general: 'Network error' });
    }
    setSaving(false);
  }

  async function toggleStatus(user) {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await apiFetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      fetchUsers();
    } catch (e) { /* ignore */ }
  }

  if (loading) return <div className="loading-overlay"><div className="spinner"></div><span>Loading users...</span></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={openCreate}>+ New User</button>
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#94A3B8' }}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td>{u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</td>
                <td>{u.email}</td>
                <td><span className="badge badge-blue">{(u.role || '').replace(/_/g, ' ')}</span></td>
                <td><span className={`badge ${u.status === 'active' ? 'badge-green' : 'badge-red'}`}>{u.status}</span></td>
                <td className="actions-cell">
                  <button onClick={() => openEdit(u)} title="Edit user" style={iconBtnBase}><EditIcon /></button>
                                    <button onClick={() => toggleStatus(u)}
                    title={u.status === 'active' ? 'Deactivate user' : 'Activate user'}
                    style={{ ...iconBtnBase, marginLeft: 6,
                      borderColor: u.status === 'active' ? 'rgba(239,68,68,0.35)' : 'rgba(16,185,129,0.35)',
                      background:  u.status === 'active' ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)',
                      color:       u.status === 'active' ? '#EF4444' : '#10B981' }}>
                    {u.status === 'active' ? <BanIcon /> : <CheckIcon />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal title={editing ? 'Edit User' : 'Create User'} isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md" dirty={dirty}>
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
        )}
        <div className="form-row">
          <FormField label="First Name" name="first_name" value={form.first_name} onChange={handleField} error={errors.first_name} required />
          <FormField label="Last Name" name="last_name" value={form.last_name} onChange={handleField} error={errors.last_name} required />
        </div>
        <FormField label="Email" name="email" type="email" value={form.email} onChange={handleField} error={errors.email} required disabled={!!editing} />
        {!editing && (
          <FormField label="Password" name="password" type="password" value={form.password} onChange={handleField} error={errors.password} required />
        )}
        <FormField label="Role" name="role" type="select" value={form.role} onChange={handleField} options={USER_ROLE_OPTIONS.map(r => ({ value: r, label: r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }))} />
        <FormField label="Status" name="status" type="select" value={form.status} onChange={handleField} options={['active', 'inactive']} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}

/* ================================================================
   Tenants Tab
   ================================================================ */
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', domain: '', status: 'active', plan: 'standard' });
  const [saving, setSaving] = useState(false);
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchTenants = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/tenants');
      if (res.ok) {
        const data = await res.json();
        setTenants(data.data || data.tenants || data || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  function handleField(name, value) { setForm(prev => ({ ...prev, [name]: value })); markDirty(); }

  function openCreate() {
    setEditing(null);
    setForm({ name: '', domain: '', status: 'active', plan: 'standard' });
    resetDirty();
    setModalOpen(true);
  }

  function openEdit(t) {
    setEditing(t);
    setForm({ name: t.name || '', domain: t.domain || '', status: t.status || 'active', plan: t.plan || 'standard' });
    resetDirty();
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url = editing ? `/api/admin/tenants/${editing.id}` : '/api/admin/tenants';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) { resetDirty(); setModalOpen(false); fetchTenants(); }
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  if (loading) return <div className="loading-overlay"><div className="spinner"></div><span>Loading...</span></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={openCreate}>+ New Tenant</button>
      </div>
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Domain</th><th>Plan</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#94A3B8' }}>No tenants found</td></tr>
            ) : tenants.map(t => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>{t.domain || '—'}</td>
                <td><span className="badge badge-blue">{t.plan || 'standard'}</span></td>
                <td><span className={`badge ${t.status === 'active' ? 'badge-green' : 'badge-red'}`}>{t.status}</span></td>
                <td className="actions-cell"><button onClick={() => openEdit(t)}>{'\u270F'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal title={editing ? 'Edit Tenant' : 'Create Tenant'} isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md" dirty={dirty}>
        <FormField label="Tenant Name" name="name" value={form.name} onChange={handleField} required />
        <FormField label="Domain" name="domain" value={form.domain} onChange={handleField} placeholder="company.bizsuite.univerk.ai" />
        <FormField label="Plan" name="plan" type="select" value={form.plan} onChange={handleField} options={['standard', 'professional', 'enterprise']} />
        <FormField label="Status" name="status" type="select" value={form.status} onChange={handleField} options={['active', 'inactive', 'suspended']} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}

/* ================================================================
   Roles Tab
   ================================================================ */
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/roles');
      if (res.ok) {
        const data = await res.json();
        setRoles(data.data || data.roles || data || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  function selectRole(role) {
    setSelectedRole(role);
    setPermissions(role.permissions || []);
    resetDirty();
  }

  function togglePermission(perm) {
    setPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
    markDirty();
  }

  async function savePermissions() {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await apiFetch(`/api/admin/roles/${selectedRole.id}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions })
      });
      resetDirty();
      fetchRoles();
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  if (loading) return <div className="loading-overlay"><div className="spinner"></div><span>Loading...</span></div>;

  return (
    <div style={{ display: 'flex', gap: 24 }}>
      <div style={{ width: 240, flexShrink: 0 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Roles</h3>
        {roles.length === 0 ? (
          <div style={{ color: '#94A3B8', fontSize: 13 }}>No custom roles defined. Default roles: {USER_ROLE_OPTIONS.join(', ')}</div>
        ) : roles.map(r => (
          <div
            key={r.id}
            onClick={() => selectRole(r)}
            style={{
              padding: '10px 14px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
              background: selectedRole?.id === r.id ? '#EFF6FF' : 'transparent',
              border: selectedRole?.id === r.id ? '1px solid #3B82F6' : '1px solid transparent',
              fontSize: 13, fontWeight: 600
            }}
          >
            {(r.name || r.role_name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </div>
        ))}
        {roles.length === 0 && (
          <div style={{ marginTop: 16 }}>
            {USER_ROLE_OPTIONS.map(r => (
              <div
                key={r}
                onClick={() => selectRole({ id: r, name: r, permissions: [] })}
                style={{
                  padding: '10px 14px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                  background: selectedRole?.id === r ? '#EFF6FF' : 'transparent',
                  border: selectedRole?.id === r ? '1px solid #3B82F6' : '1px solid transparent',
                  fontSize: 13, fontWeight: 600
                }}
              >
                {r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1 }}>
        {selectedRole ? (
          <div className="card">
            <div className="card-header">
              <h2>Permissions: {(selectedRole.name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h2>
              <button className="btn btn-primary btn-sm" onClick={savePermissions} disabled={saving}>
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
            <div className="card-body">
              <div className="permission-grid">
                {ALL_PERMISSIONS.map(perm => (
                  <div className="permission-item" key={perm}>
                    <input
                      type="checkbox"
                      checked={permissions.includes(perm)}
                      onChange={() => togglePermission(perm)}
                    />
                    <span>{perm}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>
            Select a role to manage its permissions
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================
   Settings Tab
   ================================================================ */
function SettingsTab() {
  const [settings, setSettings] = useState({
    app_name: 'BizSuite',
    company_name: '',
    company_address: '',
    company_region_id: '',
    default_currency: 'INR',
    fiscal_year_start: '01',
    session_timeout: '60',
    mfa_required: false,
    email_notifications: true,
    default_calendar_id: '',
  });
  const [calendars, setCalendars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { dirty, markDirty, resetDirty } = useFormDirty();

  // Calendars master — used by reminders, leave, etc. to drive
  // weekly-off/holiday preponement. Configured under Lookups → Calendars.
  useEffect(() => {
    apiFetch('/api/metadata/lookups?category=calendar').then(async (r) => {
      if (!r.ok) return;
      const d = await r.json();
      setCalendars((d.rows || d.lookups || []).filter((x) => x.status !== 'Inactive'));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const res = await apiFetch('/api/admin/settings');
        if (res.ok) {
          const data = await res.json();
          // API returns array of {key, value} rows — convert to flat object
          const rows = data.settings || data.data || data || [];
          if (Array.isArray(rows)) {
            const mapped = {};
            rows.forEach(r => {
              let val = r.value;
              if (val === 'true') val = true;
              else if (val === 'false') val = false;
              mapped[r.key] = val;
            });
            setSettings(prev => ({ ...prev, ...mapped }));
          } else {
            setSettings(prev => ({ ...prev, ...rows }));
          }
        }
      } catch (e) { /* use defaults */ }
    }
    load();
  }, []);

  function handleField(name, value) {
    setSettings(prev => ({ ...prev, [name]: value }));
    setSaved(false);
    markDirty();
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Backend expects individual POST per setting with {key, value, category}
      const promises = Object.entries(settings).map(([key, value]) =>
        apiFetch('/api/admin/settings', {
          method: 'POST',
          body: JSON.stringify({ key, value, category: 'app' })
        })
      );
      const results = await Promise.all(promises);
      if (results.every(r => r.ok)) { resetDirty(); setSaved(true); }
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Application Settings</h2>
      </div>
      <div className="card-body">
        <div className="form-row">
          <FormField label="Application Name" name="app_name" value={settings.app_name} onChange={(n, v) => handleField(n, v)} />
          <FormField label="Company Name" name="company_name" value={settings.company_name} onChange={(n, v) => handleField(n, v)} />
        </div>
        <FormField label="Company Address" name="company_address" type="textarea"
          value={settings.company_address} onChange={(n, v) => handleField(n, v)}
          placeholder="Full registered office address shown on invoices, letters, etc." />
        <div className="form-row">
          <RegionPicker label="Company Region" value={settings.company_region_id}
            onChange={(v) => handleField('company_region_id', v)}
            placeholder="Select region..." />
          <div></div>
        </div>
        <div className="form-row">
          <FormField label="Default Currency" name="default_currency" type="select" value={settings.default_currency} onChange={(n, v) => handleField(n, v)} options={['USD', 'EUR', 'GBP', 'SEK', 'NOK', 'INR']} />
          <FormField label="Fiscal Year Start (Month)" name="fiscal_year_start" type="select" value={settings.fiscal_year_start} onChange={(n, v) => handleField(n, v)} options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: new Date(2024, i).toLocaleString('en', { month: 'long' }) }))} />
        </div>
        {/* Default Calendar — read by the reminder scheduler when an office /
            location-specific calendar isn't set. Define calendars under
            System → Metadata → Lookups → Calendars. */}
        <div className="form-row">
          <FormField label="Default Calendar" name="default_calendar_id" type="select"
            value={settings.default_calendar_id || ''}
            onChange={(n, v) => handleField(n, v)}
            options={[
              { value: '', label: 'Mon–Fri working (built-in fallback)' },
              ...calendars.map((c) => ({ value: c.id, label: c.name })),
            ]}
            placeholder="Mon–Fri working (built-in fallback)" />
          <div></div>
        </div>
        <div className="form-row">
          <FormField label="Session Timeout (minutes)" name="session_timeout" type="number" value={settings.session_timeout} onChange={(n, v) => handleField(n, v)} min="5" max="480" />
          <div></div>
        </div>
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={settings.mfa_required} onChange={(e) => handleField('mfa_required', e.target.checked)} />
            Require MFA for all users
          </label>
        </div>
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={settings.email_notifications} onChange={(e) => handleField('email_notifications', e.target.checked)} />
            Enable email notifications
          </label>
        </div>
        <div className="form-actions">
          {saved && <span style={{ color: '#10B981', fontSize: 13, marginRight: 8 }}>Settings saved successfully</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Modern action icons + button style for the Users grid ─── */
const iconBtnBase = {
  width: 32, height: 32, padding: 0, marginRight: 6,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.04)',
  color: '#94A3B8', cursor: 'pointer',
};
function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
function BanIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
